import { RefundStatusType } from "@/types/graphql/partner.generated";

// Структурные типы, а не срез generated-типа конкретного запроса: раскладку
// и список возвратов рисуют и покупки (PurchasesListQuery), и платежи
// (PaymentsListQuery), у которых одинаковые money-поля Payment.
export interface BreakdownAmount {
  value: number;
  currency: string;
}

export interface BreakdownPayment {
  amount: BreakdownAmount;
  amountInOrganizationCurrency?: BreakdownAmount | null;
  providerFeeAmount?: BreakdownAmount | null;
  ottkitFeeAmount?: BreakdownAmount | null;
  organizationAmount?: BreakdownAmount | null;
  organizationAmountInOrganizationCurrency?: BreakdownAmount | null;
}

export interface PaymentRefund {
  id: string;
  status: RefundStatusType;
  /** Возврат учтён только у нас, деньги партнёр возвращает у провайдера. */
  manual: boolean;
  createdAt: string;
  amount: BreakdownAmount;
}

export interface BreakdownRow {
  // Ключ текста внутри `purchases.details.*` (см. partnerSeed/payments.ts).
  key: string;
  amount: BreakdownAmount | null | undefined;
  // Та же сумма в валюте организации: есть только у суммы покупки и дохода,
  // у комиссий партнёрская схема пересчёта не отдаёт.
  organizationAmount?: BreakdownAmount | null;
  strong?: boolean;
}

/**
 * Строки раскладки платежа (спека 2026-09-11 §5): сумма покупки, комиссия
 * провайдера, комиссия платформы, доход организации. Комиссия платформы
 * показывается всегда, даже нулевая. Живёт в model, потому что строками
 * пользуются оба места: таблица `PaymentBreakdown` в диалогах и подсказка у
 * колонки «Ваш доход» в списке покупок.
 */
export function buildPaymentBreakdownRows(
  payment: BreakdownPayment
): BreakdownRow[] {
  return [
    {
      key: "amountRow",
      amount: payment.amount,
      organizationAmount: payment.amountInOrganizationCurrency,
    },
    { key: "providerFeeRow", amount: payment.providerFeeAmount },
    { key: "platformFeeRow", amount: payment.ottkitFeeAmount },
    {
      key: "incomeRow",
      amount: payment.organizationAmount,
      organizationAmount: payment.organizationAmountInOrganizationCurrency,
      strong: true,
    },
  ];
}

/**
 * Последний успешный возврат по платежу: по нему диалог покупки показывает
 * дату возврата вместо даты окончания доступа (референс
 * partner/components/Rightholder/ProductPurchases/ProductPurchasesDialog/
 * ProductPurchaseDetails.js:123-136 — там это единственный `refund`).
 */
export function findLastSucceededRefund<T extends PaymentRefund>(
  refunds: readonly T[]
): T | undefined {
  return refunds
    .filter((refund) => refund.status === RefundStatusType.SucceededType)
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    )[0];
}
